import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GrAttachment } from "react-icons/gr";
import { MdOutlineBolt } from "react-icons/md";
import { Group, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { FilePreviewModal } from "@components/FilePreviewModal";

import { formatDateToString } from "@utils/dateValidation";
import { getFileTypeForDocumentation } from "@utils/documentationFileType";
import { truncateWithEllipsis } from "@utils/truncateText";

import { useAuth } from "@contexts/auth";

import { Immunization } from "@customTypes/immunization";

import { StyledLabTabTable } from "../LabTab/LabTab.styled";


const columnHelper = createColumnHelper<Partial<Immunization>>();

interface ImmunizationTabProps {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  data: Immunization[];
  isFiltered: boolean;
}

export const ImmunizationTab = ({
  isLoading,
  isError,
  isSuccess,
  data,
  isFiltered,
}: ImmunizationTabProps) => {
  const { t, i18n } = useTranslation();

  const { token } = useAuth();
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedRow, setSelectedRow] = useState<Partial<Immunization> | null>(
    null
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "fast consultation",
        cell: info => {
          return (
            <Group gap={5}>
              {info.row.original.isFastConsultation && (
                <div>
                  <MdOutlineBolt className="bolt-icon" />
                </div>
              )}
              {info.row.original?.files?.length && (
                <div>
                  <GrAttachment color="var(--mantine-color-primary-6)" />
                </div>
              )}
            </Group>
          );
        },
      }),
      columnHelper.accessor("createdAt", {
        header: t("date-created"),
        cell: info => {
          return (
            <Stack gap={5}>
              <Text fz={14}>
                {formatDateToString(info.cell.getValue() as Date)}
              </Text>
              <Text c="dimmed" fz={13}>
                {(info.cell.getValue() as Date).toLocaleTimeString(
                  i18n.language,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}
              </Text>
            </Stack>
          );
        },
      }),
      columnHelper.accessor("administeredBy", {
        header: t("user"),
        cell: info => truncateWithEllipsis(info.cell.getValue()?.fullName, 15),
      }),
      columnHelper.accessor("medication", {
        header: t("medication"),
        cell: info => truncateWithEllipsis(info.cell.getValue(), 20),
      }),
      columnHelper.display({
        id: "file type",
        header: t("file-type"),
        cell: info => {
          const { node } = getFileTypeForDocumentation(info.row.original.files);
          return node;
        },
      }),
    ],
    [data, i18n.language]
  );

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <StyledLabTabTable $hasPointerCursorOnHover>
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <th key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>

      {isSuccess && !data?.length ? (
        <tbody>
          <tr className="helper-row">
            <td colSpan={columns.length}>
              <p>
                {isFiltered
                  ? t("no-matching-immunization-records")
                  : t("empty-immunizations")}
              </p>
            </td>
          </tr>
        </tbody>
      ) : null}

      {isLoading ? (
        <tbody>
          <tr className="helper-row">
            <td colSpan={columns.length}>
              <p>{t("loading")}</p>
            </td>
          </tr>
        </tbody>
      ) : null}

      {isError && !data?.length ? (
        <tbody>
          <tr className="helper-row">
            <td colSpan={columns.length}>
              <p>{t("encountered-error")}</p>
            </td>
          </tr>
        </tbody>
      ) : null}

      <FilePreviewModal
        opened={opened}
        close={close}
        docs={
          selectedRow?.files
            ? [
                ...selectedRow.files.map(item => ({
                  uri: "https://" + item + `?token=${token}`,
                  fileName: (item as string).split("/files/")[1],
                })),
              ]
            : []
        }
      />

      <tbody>
        {table.getRowModel().rows.map(row => (
          <tr
            key={row.id}
            onClick={() => {
              setSelectedRow(row.original);
              open();
            }}>
            {row.getVisibleCells().map(cell => (
              <td key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </StyledLabTabTable>
  );
};
